import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Badge,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideX, LucidePlus, LucideUndo } from 'lucide-react';

import * as state from '../../_state/manageUserOrganizationsDialog.state';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectOrganization } from '~/shared/components/select-fields/SelectOrganization';

export const ManageUserOrganizationsDialog = () => {
  const t = useTranslations();
  const toast = useToast();

  const [isOpen, setIsOpen] = useAtom(state.$manageUserOrganizationsDialogOpen);
  const formData = useAtomValue(state.$manageUserOrganizationsFormData);
  const organizationStates = useAtomValue(state.$organizationStates);
  const availableOrganizations = useAtomValue(
    state.$availableOrganizationsFiltered,
  );
  const changesSummary = useAtomValue(state.$changesSummary);
  const hasChanges = useAtomValue(state.$hasChanges);
  const isFormValid = useAtomValue(state.$manageUserOrganizationsFormValid);
  const isSaving = useAtomValue(state.$manageUserOrganizationsDialogSaving);

  const toggleRemoveOrganization = useSetAtom(state.$toggleRemoveOrganization);
  const queueAddOrganization = useSetAtom(state.$queueAddOrganization);
  const cancelQueuedAction = useSetAtom(state.$cancelQueuedAction);
  const executeQueuedActions = useSetAtom(state.$executeQueuedActions);
  const closeDialog = useSetAtom(state.$closeManageUserOrganizationsDialog);

  const [selectedOrganizationId, setSelectedOrganizationId] =
    React.useState<string>('');

  const handleAddOrganization = React.useCallback(
    (organizationId: string | undefined) => {
      if (!organizationId) return;

      const organization = availableOrganizations.find(
        (org) => org.id === organizationId,
      );
      if (organization) {
        queueAddOrganization({
          id: organization.id,
          name: organization.name,
        });
        // Clear selection
        setSelectedOrganizationId('');
      }
    },
    [availableOrganizations, queueAddOrganization],
  );

  const handleBadgeClick = React.useCallback(
    (org: {
      id: string;
      name: string;
      state: 'normal' | 'queued-removal' | 'newly-added';
      actionId?: string;
    }) => {
      if (org.state === 'newly-added' && org.actionId) {
        // Cancel the add action
        cancelQueuedAction(org.actionId);
      } else {
        // Toggle remove for normal or undo remove for queued-removal
        toggleRemoveOrganization({
          id: org.id,
          name: org.name,
        });
      }
    },
    [cancelQueuedAction, toggleRemoveOrganization],
  );

  const handleSave = React.useCallback(async () => {
    try {
      await executeQueuedActions();
      toast.success(t.manageUserOrganizationsDialog.saveSuccess);
    } catch (_error) {
      toast.error(t.manageUserOrganizationsDialog.saveError);
    }
  }, [executeQueuedActions, toast, t]);

  const getBadgeProps = React.useCallback(
    (org: { state: 'normal' | 'queued-removal' | 'newly-added' }) => {
      switch (org.state) {
        case 'queued-removal':
          return {
            color: 'failure' as const,
            className:
              'line-through opacity-75 cursor-pointer hover:opacity-100 transition-opacity',
            title: t.manageUserOrganizationsDialog.badges.willRemove,
          };
        case 'newly-added':
          return {
            color: 'success' as const,
            className: 'cursor-pointer hover:bg-success/20 transition-colors',
            title: t.manageUserOrganizationsDialog.badges.willAdd,
          };
        default:
          return {
            color: 'primary' as const,
            className: 'cursor-pointer hover:bg-primary/20 transition-colors',
            title: t.manageUserOrganizationsDialog.badges.clickToRemove,
          };
      }
    },
    [t],
  );

  const getBadgeIcon = React.useCallback(
    (org: { state: 'normal' | 'queued-removal' | 'newly-added' }) => {
      switch (org.state) {
        case 'queued-removal':
          return <LucideUndo className="ml-1 size-3" />;
        case 'newly-added':
          return <LucideX className="ml-1 size-3" />;
        default:
          return (
            <LucideX className="ml-1 size-3 opacity-0 transition-opacity group-hover:opacity-100" />
          );
      }
    },
    [],
  );

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
      className="@ManageUserOrganizationsDialog"
    >
      <DialogContent className="min-w-[600px]">
        <DialogHeader slot="title">
          <DialogTitle>{t.manageUserOrganizationsDialog.title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 p-4">
          {/* User info display */}
          <div className="rounded-md bg-neutral/5 p-3">
            <div className="text-sm text-neutral/60">
              {t.manageUserOrganizationsDialog.userInfo}
            </div>
            <div className="font-medium text-neutral/90">
              {formData.userName}
            </div>
          </div>

          {/* Current Organizations Section */}
          <div>
            <div className="mb-3 font-medium text-neutral/70">
              {t.manageUserOrganizationsDialog.currentOrganizations}
            </div>

            {organizationStates.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {organizationStates.map((org) => {
                  const badgeProps = getBadgeProps(org);
                  return (
                    <div key={org.id} className="group">
                      <Badge
                        {...badgeProps}
                        size="default"
                        onClick={() => handleBadgeClick(org)}
                      >
                        <span className="flex items-center">
                          {org.name}
                          {getBadgeIcon(org)}
                        </span>
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-neutral/50 italic">
                No organizations assigned
              </div>
            )}
          </div>

          {/* Add Organization Section */}
          <div>
            <div className="mb-3 font-medium text-neutral/70">
              {t.manageUserOrganizationsDialog.addOrganization}
            </div>
            <div className="flex items-center gap-3">
              <SelectOrganization
                value={selectedOrganizationId}
                onChange={handleAddOrganization}
                className="flex-1"
                placeholder={
                  t.manageUserOrganizationsDialog.fields.organization
                    .placeholder
                }
              />
              {availableOrganizations &&
                availableOrganizations.length === 0 && (
                  <div className="text-sm text-neutral/50 italic">
                    {t.manageUserOrganizationsDialog.noOrganizationsAvailable}
                  </div>
                )}
            </div>
          </div>

          {/* Changes Summary */}
          {hasChanges && (
            <div className="rounded-md border border-primary/20 bg-primary/5 p-3">
              <div className="mb-1 text-sm font-medium text-primary/80">
                Pending Changes:
              </div>
              <div className="text-sm text-primary/70">
                {changesSummary.addCount > 0 && (
                  <div className="flex items-center gap-1">
                    <LucidePlus className="size-3 text-success/60" />
                    {t.manageUserOrganizationsDialog.pendingChanges.adding.replace(
                      '{count}',
                      changesSummary.addCount.toString(),
                    )}
                  </div>
                )}
                {changesSummary.removeCount > 0 && (
                  <div className="flex items-center gap-1">
                    <LucideX className="size-3 text-failure/60" />
                    {t.manageUserOrganizationsDialog.pendingChanges.removing.replace(
                      '{count}',
                      changesSummary.removeCount.toString(),
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            aria-label={t.manageUserOrganizationsDialog.actions.cancel}
            variant="outline"
            color="neutral"
            onPress={closeDialog}
            isDisabled={isSaving}
          >
            {t.manageUserOrganizationsDialog.actions.cancel}
          </Button>
          <Button
            aria-label={t.manageUserOrganizationsDialog.actions.save}
            variant="solid"
            color="primary"
            onPress={handleSave}
            isDisabled={!isFormValid || isSaving}
            isLoading={isSaving}
          >
            {t.manageUserOrganizationsDialog.actions.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
